/**
 * Catalog access and derived indexes over src/product-config.js.
 *
 * RUNTIME CONSTRAINT: this module (and everything else in src/lib/pricing/) must run
 * unchanged in three places -- CRA/webpack, the Cloudflare Workers runtime, and Node via
 * the Express dev server. So: plain ESM, no Node built-ins, no `require`, no process.env,
 * no Buffer, no top-level I/O. Keep it pure.
 *
 * Why this lives under src/ rather than a repo-root shared/: CRA's webpack config applies
 * ModuleScopePlugin(paths.appSrc), which hard-fails any relative import that escapes src/.
 * Only webpack is fenced -- Wrangler's esbuild and Node both resolve upward into src/
 * happily (verified with a Pages Functions build probe).
 */

import {
  primaryProducts,
  addonSubscriptions,
  oneTimeProducts,
  setupFeeConfig,
  questions,
} from '../../product-config.mjs';

export {
  primaryProducts,
  addonSubscriptions,
  oneTimeProducts,
  setupFeeConfig,
  questions,
};

/** Kinds of catalog entry a lookup key can come from. */
export const ITEM_KIND = {
  PRIMARY: 'primary',
  ADDON: 'addon',
  ONE_TIME: 'one-time',
  QUESTION: 'question',
  SETUP_FEE: 'setup-fee',
};

/**
 * Walk the whole catalog and emit one descriptor per lookup key.
 *
 * This is the allowlist the server validates against. Before this existed the server had
 * no idea what products were legitimate -- product-config.js is ESM and app.js could never
 * import it -- so it forwarded whatever lookup keys the client sent straight to Stripe.
 *
 * @returns {Array<{lookup_key: string, price_display: number, kind: string,
 *   productId: string, termId: ?string, requiresPrimary: ?string[], label: string}>}
 */
function collectCatalogEntries() {
  const entries = [];

  primaryProducts.forEach((plan) => {
    (plan.terms || []).forEach((term) => {
      entries.push({
        lookup_key: term.lookup_key,
        price_display: term.price_display,
        kind: ITEM_KIND.PRIMARY,
        productId: plan.id,
        termId: term.id,
        requiresPrimary: null,
        label: plan.name,
      });
    });
  });

  addonSubscriptions.forEach((addon) => {
    (addon.terms || []).forEach((term) => {
      entries.push({
        lookup_key: term.lookup_key,
        price_display: term.price_display,
        kind: ITEM_KIND.ADDON,
        productId: addon.id,
        termId: term.id,
        requiresPrimary: null,
        label: addon.name,
      });
    });
  });

  oneTimeProducts.forEach((product) => {
    entries.push({
      lookup_key: product.lookup_key,
      price_display: product.price_display,
      kind: ITEM_KIND.ONE_TIME,
      productId: product.id,
      termId: null,
      requiresPrimary: null,
      label: product.name,
    });
  });

  // Question-driven upsells. conditionalPricing is keyed by primary plan id, which is what
  // scopes a key to a plan: 'seo_content_business' is only reachable with business-plan
  // selected, so the server can reject a starter-plan cart that smuggles it in.
  questions.forEach((question) => {
    (question.answers || []).forEach((answer) => {
      const addProduct = answer.addProduct;
      if (!addProduct) return;

      const kindForType = addProduct.type === 'subscription' ? ITEM_KIND.QUESTION : ITEM_KIND.QUESTION;

      if (addProduct.conditionalPricing) {
        // Group plan ids by lookup key: two plans may legitimately share one key.
        const byKey = new Map();
        Object.entries(addProduct.conditionalPricing).forEach(([planId, priceInfo]) => {
          if (!priceInfo?.lookup_key) return;
          if (!byKey.has(priceInfo.lookup_key)) {
            byKey.set(priceInfo.lookup_key, { priceInfo, planIds: [] });
          }
          byKey.get(priceInfo.lookup_key).planIds.push(planId);
        });

        byKey.forEach(({ priceInfo, planIds }, lookupKey) => {
          entries.push({
            lookup_key: lookupKey,
            price_display: priceInfo.price_display,
            kind: kindForType,
            productId: `${question.id}:${answer.id}`,
            termId: null,
            requiresPrimary: planIds,
            label: answer.label || question.title,
            isSubscription: addProduct.type === 'subscription',
          });
        });
      } else if (addProduct.fixedProduct?.lookup_key) {
        entries.push({
          lookup_key: addProduct.fixedProduct.lookup_key,
          price_display: addProduct.fixedProduct.price_display,
          kind: kindForType,
          productId: `${question.id}:${answer.id}`,
          termId: null,
          requiresPrimary: null,
          label: answer.label || question.title,
          isSubscription: addProduct.type === 'subscription',
        });
      }
    });
  });

  if (setupFeeConfig?.monthlySetupFee?.lookup_key) {
    entries.push({
      lookup_key: setupFeeConfig.monthlySetupFee.lookup_key,
      price_display: setupFeeConfig.monthlySetupFee.price_display,
      kind: ITEM_KIND.SETUP_FEE,
      productId: 'setup-fee',
      termId: 'monthly',
      requiresPrimary: null,
      label: setupFeeConfig.monthlySetupFee.description || 'Setup fee',
    });
  }

  return entries;
}

/**
 * Build the lookup-key index, failing loudly on a duplicate.
 *
 * Duplicates throw rather than warn on purpose. 'Stock Photos' shipped with
 * lookup_key: 'setup_fee' -- identical to the real setup fee -- and was invisible only
 * because that UI section was disabled. Enabling it would have double-charged customers
 * $99 under the wrong label. A failed module load is a far cheaper failure than a billing
 * incident, so this converts a whole bug class into a boot error.
 */
export function buildCatalogIndex() {
  const entries = collectCatalogEntries();
  const byLookupKey = new Map();
  const duplicates = [];

  entries.forEach((entry) => {
    if (!entry.lookup_key) return;

    const existing = byLookupKey.get(entry.lookup_key);
    if (existing) {
      // Identical descriptors are fine (e.g. two plans sharing one conditional price);
      // a collision across different products is not.
      if (existing.productId !== entry.productId) {
        duplicates.push(`'${entry.lookup_key}' used by both '${existing.productId}' and '${entry.productId}'`);
      }
      return;
    }
    byLookupKey.set(entry.lookup_key, entry);
  });

  if (duplicates.length > 0) {
    throw new Error(
      'product-config.js has colliding lookup_keys, which would mis-bill customers:\n  ' +
        duplicates.join('\n  ') +
        '\nGive each product its own lookup_key and create the matching Stripe price.'
    );
  }

  const primaryLookupKeys = new Set(
    entries.filter((e) => e.kind === ITEM_KIND.PRIMARY).map((e) => e.lookup_key)
  );

  return {
    byLookupKey,
    primaryLookupKeys,
    setupFeeLookupKey: setupFeeConfig?.monthlySetupFee?.lookup_key || null,
  };
}

/**
 * Shared index for callers that do not need a fresh build.
 * Computed lazily so an invalid catalog throws at first use with a clear stack, rather
 * than at import time inside an unrelated module.
 */
let cachedIndex = null;
export function getCatalogIndex() {
  if (!cachedIndex) cachedIndex = buildCatalogIndex();
  return cachedIndex;
}

/** Look up a primary plan and one of its terms. */
export function findPlanTerm(planId, termId) {
  const plan = primaryProducts.find((p) => p.id === planId);
  if (!plan) return null;
  const term = (plan.terms || []).find((t) => t.id === termId);
  if (!term) return null;
  return { plan, term };
}
